import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Mail, FileText, Paperclip, X, Link2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { fmtDate, fmtAddress } from "@/lib/format";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const card = "bg-white rounded-[16px] p-5 border border-slate-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)]";
const ORIGIN_CLS = { "Recebido por email": "bg-sky-50 text-sky-700", "Página do cliente": "bg-emerald-50 text-emerald-700", Equipa: "bg-slate-100 text-slate-600" };

function DocRow({ d }) {
  return (
    <li className="flex items-center gap-3 py-3 border-t border-slate-100 first:border-t-0" data-testid={`review-doc-${d.ref}-${d.key}`}>
      <FileText size={16} className="text-slate-400 shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="text-[15px] font-medium text-ec-green truncate">{d.name}</div>
        <div className="text-sm text-slate-500 truncate">{d.ref} · {fmtAddress(d.address)}{d.from ? ` · de ${d.from}` : ""}</div>
      </div>
      <span className={`hidden sm:inline text-xs font-semibold px-2 py-1 rounded-full shrink-0 ${ORIGIN_CLS[d.origin]}`} data-testid="review-doc-origin">{d.origin}</span>
      <Link to={`/processo/${d.process_id}#doc-${d.key}`} className="text-sm font-semibold px-3 py-1.5 rounded-lg border border-slate-200 text-ec-green hover:bg-slate-50 shrink-0" data-testid={`review-doc-open-${d.ref}-${d.key}`}>Rever</Link>
    </li>
  );
}

function EmailRow({ e, procs, onDone }) {
  const [pid, setPid] = useState(e.process_id || "");
  const act = async (fn, ok) => { try { await fn(); toast.success(ok); onDone(); } catch (err) { toast.error(apiErr(err.response?.data?.detail)); } };
  return (
    <li className="py-4 border-t border-slate-100 first:border-t-0" data-testid={`unclassified-${e.id}`}>
      <div className="flex items-start gap-3">
        <Mail size={16} className="text-slate-400 mt-1 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-[15px] font-medium text-ec-green truncate">{e.subject || "(sem assunto)"}</div>
          <div className="text-sm text-slate-500">De {e.from_addr} · para {e.to_addr} · {fmtDate(e.created_at)}</div>
          {e.matched_ref && <div className="text-xs text-amber-700 mt-0.5">Remetente desconhecido no processo {e.matched_ref}</div>}
          <div className="flex flex-wrap gap-1.5 mt-2">
            {e.attachments.map((a) => <span key={a.file_id} className="inline-flex items-center gap-1 text-xs bg-slate-100 text-slate-600 rounded-md px-2 py-1"><Paperclip size={11} /> {a.filename}</span>)}
            {e.ignored.map((a) => <span key={a.filename} className="text-xs bg-red-50 text-red-700 rounded-md px-2 py-1">Anexo ignorado: {a.reason} · {a.filename}</span>)}
          </div>
        </div>
      </div>
      <div className="mt-3 flex flex-col sm:flex-row gap-2 sm:pl-7">
        <Select value={pid} onValueChange={setPid}>
          <SelectTrigger className="sm:w-80 h-10" data-testid={`unclassified-process-${e.id}`}><SelectValue placeholder="Escolher processo" /></SelectTrigger>
          <SelectContent>{procs.map((p) => <SelectItem key={p.id} value={p.id}>{p.ref} · {p.property_address}</SelectItem>)}</SelectContent>
        </Select>
        <button disabled={!pid} onClick={() => act(() => api.post(`/inbox/${e.id}/assign`, { process_id: pid }), "Email associado ao processo")}
          className="inline-flex items-center justify-center gap-1.5 h-10 px-4 rounded-lg bg-ec-green text-white text-sm font-semibold disabled:opacity-40" data-testid={`unclassified-assign-${e.id}`}><Link2 size={14} /> Associar a um processo</button>
        <button onClick={() => act(() => api.post(`/inbox/${e.id}/reject`), "Email rejeitado")}
          className="inline-flex items-center justify-center gap-1.5 h-10 px-4 rounded-lg border border-slate-200 text-slate-600 text-sm font-semibold hover:bg-slate-50" data-testid={`unclassified-reject-${e.id}`}><X size={14} /> Rejeitar</button>
      </div>
    </li>
  );
}

export function ReviewQueue({ procs }) {
  const { user } = useAuth();
  const [q, setQ] = useState(null);
  const load = () => api.get("/review-queue").then(({ data }) => setQ(data)).catch(() => setQ({ documents: [], unclassified: [] }));
  useEffect(() => { load(); }, []);
  if (!q) return null;
  const showUnclassified = user.role !== "consultor" || q.unclassified.length > 0;
  return (
    <div className={card} data-testid="review-queue">
      <h2 className="font-heading text-xl text-ec-green">Documentos para rever · {q.documents.length}</h2>
      {q.documents.length ? <ul className="mt-2">{q.documents.map((d) => <DocRow key={`${d.process_id}-${d.key}`} d={d} />)}</ul>
        : <p className="text-sm text-slate-500 mt-2" data-testid="review-empty">Nada para rever.</p>}
      {showUnclassified && (
        <div className="mt-6 pt-5 border-t border-slate-100" data-testid="unclassified-section">
          <h3 className="font-heading text-lg text-ec-green">Por classificar · {q.unclassified.length}</h3>
          <p className="text-sm text-slate-500">Emails de remetentes desconhecidos ou sem número de processo.</p>
          {q.unclassified.length ? <ul className="mt-2">{q.unclassified.map((e) => <EmailRow key={e.id} e={e} procs={procs} onDone={load} />)}</ul>
            : <p className="text-sm text-slate-400 mt-2">Sem emails por classificar.</p>}
        </div>
      )}
    </div>
  );
}
